import { Component, OnInit, AfterViewInit, OnDestroy, Input, Output, HostListener, EventEmitter, ViewChild } from '@angular/core';
import { NgbCarousel, NgbSlideEvent, NgbSlideEventDirection } from '@ng-bootstrap/ng-bootstrap';
import { AppSettingService } from '../../app.service';
import { ETerritoryCardDisplayMode, ITerritoryCard } from '../../territoryCard/territory-card.service';

export interface IObjectCarouselAction {
	name: string,
	icon: string,
	toolTip: string,
	html: string
}

export class ObjectCarouselAction implements IObjectCarouselAction {
	name = "";
	icon = "";
	toolTip = "";
	html = "";

	constructor(_name: string, _icon: string, _html: string,  _toolTip: string) {
		if (_icon.trim().length > 0 && _html.trim().length > 0) {
			throw "Please use icon or provide html";
		}
		this.name = _name.trim();
		this.icon = _icon.trim();
		this.toolTip = _toolTip.trim();
		this.html = _html.trim();
	}
}

@Component({
	template: "",
	standalone: false
})
export abstract class ObjectCarouselComponent implements OnInit, AfterViewInit, OnDestroy {
	@Input() objectId: string = "";
	@Output() hasData: EventEmitter<boolean> = new EventEmitter();
	@Output() onClose: EventEmitter<any> = new EventEmitter(); //when close for isPopup

	protected componentName: string = ""; // component name, used in log to indicate component is loaded
	public objectName: string = ""; // the title
	public isPopup: boolean = false; // when true, display card in mobile view
	public highlightNoData: boolean = false; // when true, use blue background
	protected downloadComplete: boolean = false; // comppnent has completed all downloads
	protected downloading: boolean = false; // used in component only, indicate component is download data
	public retrievingData: boolean = false; // first download, display spinning
	public noDataWidget: boolean = false; // indicate no data found
	public noDataMessage: string = AppSettingService.noDataMessage; // what messasge to display when no data
	public resultMessage: string = "" // if there is no result, display resultMessage instead. This is for component with filter and result is filtered and cause no data
	protected removeUnfollow: boolean = false; // when true, remove unfollow card from objectItems and shift cards
	protected isInitializedByHostListener = false; //here in case child classes need to know when the hostlistener happened from this class.
	constructor() {}

	public toggled: boolean = AppSettingService.widgetMobile();
	public showMinimize: boolean = AppSettingService.widgetMobile();

	@HostListener('window:resize', ['$event']) onResize(event: any) {
		this.showMinimize = this.isPopup ? true : AppSettingService.widgetMobile();
		this.isInitializedByHostListener = true; //can be set back to false by child if child needs to know when it's ngOnInit is triggered from here.
		this.ngOnInit();
	}

	public toggleCollapse(): void {
		if (this.isPopup) {
			this.onClose.emit();
		}
		else {
			this.toggled = !this.toggled;
			this.ngOnInit();
		}
	}

	get componentId(): string {
		return this.objectName === "" ? "dvUNKNOWN" : "dv" + this.objectName.replace(" ", "").replace("'", "").toUpperCase();
	}


	//#region slides
	@ViewChild("myCarousel") myCarousel: NgbCarousel;
	private _slides: any[] = [];
	private _objectItems: ITerritoryCard[] = [];
	public displayMode = ETerritoryCardDisplayMode.Auto;
	protected readonly itemsPerSlide: number = 3;
	protected readonly limit: number = 15;
	protected offset: number = 0;
	private _resizing: boolean = false;

	get slides(): any[] {
		return this._slides;
	}

	get objectItems(): ITerritoryCard[] {
		return this._objectItems;
	}

	public onSlide(e: NgbSlideEvent) {
		if (e.direction != null ) {
			if (!this.downloadComplete && Number(e.current) === this.slides.length - 2) {
				this.downloadData();
			}
		}
	}

	get resizing(): boolean {
		return this._resizing;
	}

	private updateSlides(): void {
		this._slides = Array.from(Array(Math.ceil(this.objectItems.length / this.itemsPerSlide)).keys());
	}

	public followToggled(args: any, itemIndex: number, slideIndex: number): void {
		if (!this.removeUnfollow || itemIndex == null || itemIndex < 0 || slideIndex == null || slideIndex < 0 || args == null || args.Status == null) {
			return;
		}
		if (args.Status == false && itemIndex < this._objectItems.length) {
			this._resizing = true;
			try {
				this._objectItems.splice(itemIndex, 1);
				this.updateSlides();
				if (this.objectItems.length === 0) {
					this.noDataWidget = true;
				}
				else {
					if (slideIndex > this.slides.length - 1) {
						this.myCarousel.activeId = (this.slides.length - 1).toString();
					}
				}
			}
			catch (ex) {
				console.log(ex);
			}
			finally {
				this._resizing = false;
			}
		}
	};

	protected downloadData(): void {}

	protected addObjectItem(item: ITerritoryCard) {
		this._objectItems.push(item);
		this.updateSlides();
	}

	protected initCarousel(): void {
		this.downloadComplete = false;
		this.noDataWidget = false;
		this.downloading = false;
		this.retrievingData = false;
		this.resultMessage = "";
		this._objectItems = [];
		this._slides = [];
		this.offset = 0;
		this.downloading = false;
	}
	//#endregion slides

	//#region action icon
	private _actionStatus: boolean = false;
	private _actions: ObjectCarouselAction[] = [];
	private _secondaryActions: ObjectCarouselAction[] = [];

	get actions(): ObjectCarouselAction[] {
		return this._actions;
	}

	get secondaryActions(): ObjectCarouselAction[] {
		return this._secondaryActions;
	}

	protected addAction(type: "filter" | "viewall"): void {
		switch (type) {
			case "filter":
				this._actions.push(new ObjectCarouselAction("filter", "icnSliders", "", "Filter"));
				break;
			case "viewall":
				this._actions.push(new ObjectCarouselAction("viewall", "icnClipboardList", "", "View All"));
				break;
		}
	}

	protected addCustomAction(icon: ObjectCarouselAction, append:boolean, isSecondary: boolean): void {
		if (icon instanceof ObjectCarouselAction) {
			if (append) {
				if (isSecondary) {
					this._secondaryActions.push(icon);
				}
				else {
					this._actions.push(icon);
				}
			}
			else {
				if (isSecondary) {
					this._secondaryActions.unshift(icon);
				}
				else {
					this._actions.unshift(icon);
				}
			}
		}
		else {
			throw "Please ensure the data type is ObjectCarouselAction";
		}
	}

	protected removeAction(name: string) : void {
		this._actions = this._actions.filter(x => x.name != name);
		this._secondaryActions = this._secondaryActions.filter(x => x.name != name);
	}

	protected displayAction(display:boolean): void {
		this._actionStatus = display;
	}

	public getActionStatus(): boolean {
		return this._actionStatus;
	}

	public clickAction(act: IObjectCarouselAction) {
		console.log(act);
	}
	//#endregion action icon

	public clickNoDataMessage(e:any): void {
	}

	ngOnDestroy(): void {}

	ngAfterViewInit(): void {}

	private hasLoaded: boolean = false;
	private firstLoad: boolean = true;
	ngOnInit(): void {
		if (this.firstLoad) {
			this.firstLoad = false;
			this.toggled = this.isPopup ? false : AppSettingService.widgetMobile();
			this.showMinimize = this.isPopup ? true : AppSettingService.widgetMobile();
			this.displayMode = this.isPopup ? ETerritoryCardDisplayMode.Mobile : ETerritoryCardDisplayMode.Auto;
		}
		if (this.hasLoaded === false) {
			if ((this.showMinimize && !this.toggled) || !this.showMinimize) {
				this.hasLoaded = true;
				this.downloadData();
			}
		}
	}
}
